import {
  Machine,
  sendParent,
  DoneInvokeEvent,
  State,
  Actor,
} from 'xstate';
import { assign } from '@xstate/immer';
import cogoToast from 'cogo-toast';

import { Subscription, FacebookSubscription } from '~/api/models/leadads/subscription';
import {
  getSubscription,
  createSubscription,
  resetSubscription,
  Connections,
} from '~/api/routes/leadad/subscription';

import { sendErrorService } from '~/services/error';

interface SubscriptionSchema {
  states: {
    status: {
      states: {
        init: {};
        connected: {};
        notConnected: {};
        notConnectedFinal: {};
      };
    };
    subscription: {
      states: {
        idle: {};
        getting: {};
        resetting: {};
        creating: {};
      };
    };
  };
}

export interface SubscriptionContext {
  subscriptionId?: string;
  retriesLeft: number;
}

export type SubscriptionState = State<SubscriptionContext, SubscriptionEvent, SubscriptionSchema>;
export type SubscriptionActor = Actor<SubscriptionState, SubscriptionEvent>;

export type InitEvent = { type: 'SUBSCRIPTION.init', pageId: string, accountId: string };
export type SubscriptionEvent =
  | { type: 'SUBSCRIPTION.reset'; subscription: Omit<Subscription, 'id'> }
  | { type: 'SUBSCRIPTION.create'; subscription: Omit<Subscription, 'id'> };

type LeadAdSubscriptionEvent = SubscriptionEvent | InitEvent;

const isSubscribed = (_, evt) => evt.data.hasSubscription;

/**
 * This machine is the base configuration for an actor that will spawned by the parent
 * machine. It is in charge of updating the subscription of lead types and managing said subscription.
 * The parent machine shouldn't have to be concerned about the internal process of creating a subscription,
 * it just has to know that a subscription will be and has been created
 */
const subscriptionMachine = Machine<SubscriptionContext, SubscriptionSchema, LeadAdSubscriptionEvent>({
  id: 'lead-router-subscriptions',
  type: 'parallel',
  on: {
    'SUBSCRIPTION.init': {
      target: ['.subscription.getting', '.status.init'],
      actions: assign((ctx) => {
        ctx.subscriptionId = undefined;
        ctx.retriesLeft = 0;
      }),
    },
  },
  context: {
    subscriptionId: undefined,
    retriesLeft: 3,
  },
  states: {
    status: {
      initial: 'init',
      states: {
        init: {},
        connected: {
          entry: sendParent('SUBSCRIPTION.connected'),
          on: {
            'SUBSCRIPTION.reset': '#lead-router-subscriptions.subscription.resetting',
          },
        },
        notConnected: {
          entry: [
            sendParent('SUBSCRIPTION.notConnected'),
            assign((ctx) => { ctx.retriesLeft--; }),
          ],
          on: {
            'SUBSCRIPTION.create': '#lead-router-subscriptions.subscription.creating',
          },
        },
        notConnectedFinal: {
          entry: () => cogoToast.warn('There was an issue setting your lead router subscription'),
        },
      },
    },
    subscription: {
      initial: 'idle',
      states: {
        idle: {},
        getting: {
          invoke: {
            id: 'get-subscription',
            src: (_, evt: InitEvent) => getSubscription(evt.pageId, evt.accountId),
            onDone: [
              {
                target: ['idle', '#lead-router-subscriptions.status.connected'],
                cond: isSubscribed,
                actions: [
                  assign((ctx, evt: DoneInvokeEvent<Connections>) => {
                    ctx.subscriptionId = evt.data.id;
                  }),
                  sendParent((_, evt: DoneInvokeEvent<Connections>) => ({
                    type: 'SUBSCRIPTION.connected',
                    leadType: evt.data.leadType,
                  })),
                ],
              },
              {
                target: ['idle', '#lead-router-subscriptions.status.notConnected'],
              },
            ],
            onError: {
              target: ['idle', '#lead-router-subscriptions.status.notConnected'],
              actions: (_, evt) => {
                sendErrorService(new Error('Error getting subscription'), 'lead-ad-subscriptions', evt.data);
              },
            },
          },
        },
        creating: {
          invoke: {
            id: 'create-subscription',
            src: (ctx, evt: SubscriptionEvent) => createSubscription(evt.subscription),
            onDone: {
              target: ['idle', '#lead-router-subscriptions.status.connected'],
              actions: assign((ctx, evt: DoneInvokeEvent<FacebookSubscription>) => {
                ctx.subscriptionId = evt.data.id;
                ctx.retriesLeft = 0;
              }),
            },
            onError: [
              {
                cond: ctx => ctx.retriesLeft !== 0,
                target: ['idle', '#lead-router-subscriptions.status.notConnected'],
                actions: (_, evt) => {
                  sendErrorService(new Error('Error creating subscription'), 'lead-ad-subscriptions', evt.data);
                },
              },
              {
                target: ['idle', '#lead-router-subscriptions.status.notConnectedFinal'],
                actions: (_, evt) => {
                  sendErrorService(new Error('Error creating subscription'), 'lead-ad-subscriptions', evt.data);
                },
              },
            ],
          },
        },
        resetting: {
          invoke: {
            id: 'reset-subscription',
            src: (ctx, evt: SubscriptionEvent) => resetSubscription({ ...evt.subscription, id: ctx.subscriptionId }),
            onDone: {
              target: 'idle',
              actions: assign((ctx, evt: DoneInvokeEvent<FacebookSubscription>) => {
                ctx.subscriptionId = evt.data.id;
                ctx.retriesLeft = 0;
              }),
            },
            onError: [
              {
                cond: ctx => ctx.retriesLeft !== 0,
                target: ['idle', '#lead-router-subscriptions.status.notConnected'],
                actions: (_, evt) => {
                  sendErrorService(new Error('Error resetting subscription'), 'lead-ad-subscriptions', evt.data);
                },
              },
              {
                target: ['idle', '#lead-router-subscriptions.status.notConnectedFinal'],
                actions: (_, evt) => {
                  sendErrorService(new Error('Error resetting subscription'), 'lead-ad-subscriptions', evt.data);
                },
              },
            ],
          },
        },
      },
    },
  },
});

export default subscriptionMachine;
